import React, { useState, useEffect } from 'react';
import { API } from '../utils/api';
import LucideIcon from './LucideIcon';

interface AdminChallenge {
  id: string;
  title: string;
  description: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced' | 'expert';
  category: string;
  points: number;
  tags: string[];
}

interface LLMParams {
  temperature: number;
  top_k: number;
  top_p: number;
  max_tokens: number;
}

interface LLMConfig {
  playground: LLMParams;
  generation: LLMParams;
  evaluation: LLMParams;
}

const panel: React.CSSProperties = {
  background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 22,
};

const sliderLabel: React.CSSProperties = {
  display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6,
};

const fieldLabel: React.CSSProperties = {
  display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6,
};

const fieldInput: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', padding: '10px 13px', fontSize: 13.5,
  color: 'var(--text-primary)', background: 'var(--surface-sunken)', border: '1px solid var(--border)',
  borderRadius: 10, outline: 'none', fontFamily: 'inherit',
};

/**
 * Same escalating-accent language as config/badges.ts's RARITY_STYLE
 * (neutral -> volt -> ember -> gold) -- reused here so "expert" reads as the
 * hardest tier the same way "legendary" reads as the rarest badge, rather
 * than inventing a second color meaning on this one page.
 */
const DIFFICULTY_STYLE: Record<AdminChallenge['difficulty'], { border: string; bg: string; fg: string }> = {
  beginner: { border: 'var(--border-strong)', bg: 'var(--surface-sunken)', fg: 'var(--text-secondary)' },
  intermediate: { border: 'var(--volt)', bg: 'var(--volt-soft)', fg: 'var(--volt-strong)' },
  advanced: { border: 'var(--ember)', bg: 'var(--ember-soft)', fg: 'var(--ember-strong)' },
  expert: { border: 'var(--gold)', bg: 'oklch(0.8 0.15 85 / 0.16)', fg: 'oklch(0.55 0.13 75)' },
};

const CONFIG_SECTIONS: { key: keyof LLMConfig; title: string; description: string }[] = [
  { key: 'playground', title: 'Playground', description: 'Default sampling used when a learner experiments in the Playground.' },
  { key: 'generation', title: 'Challenge Generation', description: 'Parameters used when the AI writes a new challenge scenario.' },
  { key: 'evaluation', title: 'Challenge Evaluation', description: "Parameters used when the AI grades a learner's submission." },
];

const difficulties: AdminChallenge['difficulty'][] = ['beginner', 'intermediate', 'advanced', 'expert'];
const categories = ['basics', 'creativity', 'precision', 'system-design', 'role-playing', 'data-extraction', 'reasoning'];

const AdminPanel: React.FC = () => {
  const [challenges, setChallenges] = useState<AdminChallenge[]>([]);
  const [challengesLoading, setChallengesLoading] = useState(true);
  const [challengesError, setChallengesError] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const [selectedChallenge, setSelectedChallenge] = useState<AdminChallenge | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [savingChallenge, setSavingChallenge] = useState(false);
  const [modalError, setModalError] = useState('');

  const [llmConfig, setLlmConfig] = useState<LLMConfig>({
    playground: { temperature: 0.7, top_k: 40, top_p: 0.9, max_tokens: 1000 },
    generation: { temperature: 0.8, top_k: 50, top_p: 0.95, max_tokens: 2000 },
    evaluation: { temperature: 0.3, top_k: 20, top_p: 0.8, max_tokens: 500 },
  });
  const [llmLoading, setLlmLoading] = useState(true);
  const [llmError, setLlmError] = useState('');
  const [llmSuccess, setLlmSuccess] = useState('');
  const [savingLlm, setSavingLlm] = useState(false);

  const [activeTab, setActiveTab] = useState<'challenges' | 'llm'>('challenges');

  useEffect(() => {
    loadChallenges();
    loadLLMConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadChallenges = async () => {
    setChallengesLoading(true);
    setChallengesError('');
    try {
      const response = await API.get('/admin/challenges');
      setChallenges(Array.isArray(response) ? response : []);
    } catch (error: any) {
      setChallengesError(error?.message || 'Could not load challenges. Try again.');
    } finally {
      setChallengesLoading(false);
    }
  };

  const loadLLMConfig = async () => {
    setLlmLoading(true);
    setLlmError('');
    try {
      const response = await API.get('/admin/llm-config');
      if (response && response.playground && response.generation && response.evaluation) {
        setLlmConfig(response);
      }
    } catch (error: any) {
      setLlmError(error?.message || 'Could not load the AI configuration. Showing the last known values below.');
    } finally {
      setLlmLoading(false);
    }
  };

  const saveLLMConfig = async () => {
    setSavingLlm(true);
    setLlmError('');
    setLlmSuccess('');
    try {
      await API.put('/admin/llm-config', llmConfig);
      setLlmSuccess('AI configuration saved.');
      window.setTimeout(() => setLlmSuccess(''), 4000);
    } catch (error: any) {
      setLlmError(error?.message || 'Could not save the AI configuration. Try again.');
    } finally {
      setSavingLlm(false);
    }
  };

  const updateParam = (section: keyof LLMConfig, field: keyof LLMParams, value: number) => {
    setLlmConfig({ ...llmConfig, [section]: { ...llmConfig[section], [field]: value } });
  };

  const openNewChallenge = () => {
    setModalError('');
    setSelectedChallenge({ id: '', title: '', description: '', difficulty: 'beginner', category: 'basics', points: 100, tags: [] });
    setIsEditing(false);
  };

  const openEditChallenge = (challenge: AdminChallenge) => {
    setModalError('');
    setSelectedChallenge(challenge);
    setIsEditing(true);
  };

  const closeModal = () => {
    setSelectedChallenge(null);
    setIsEditing(false);
    setModalError('');
  };

  const handleSaveChallenge = async () => {
    if (!selectedChallenge) return;
    setSavingChallenge(true);
    setModalError('');
    try {
      if (isEditing) {
        await API.put(`/admin/challenges/${selectedChallenge.id}`, selectedChallenge);
      } else {
        await API.post('/admin/challenges', selectedChallenge);
      }
      await loadChallenges();
      setSelectedChallenge(null);
      setIsEditing(false);
    } catch (error: any) {
      setModalError(error?.message || 'Could not save the challenge. Try again.');
    } finally {
      setSavingChallenge(false);
    }
  };

  const handleDeleteChallenge = async (id: string) => {
    setDeletingId(id);
    setChallengesError('');
    try {
      await API.delete(`/admin/challenges/${id}`);
      await loadChallenges();
    } catch (error: any) {
      setChallengesError(error?.message || 'Could not delete the challenge. Try again.');
    } finally {
      setDeletingId(null);
      setConfirmDeleteId(null);
    }
  };

  const tabButtonStyle = (tab: 'challenges' | 'llm'): React.CSSProperties => ({
    fontSize: 13, fontWeight: 600, padding: '9px 18px', borderRadius: 10, cursor: 'pointer',
    border: `1px solid ${activeTab === tab ? 'var(--ember)' : 'var(--border)'}`,
    background: activeTab === tab ? 'var(--ember)' : 'var(--surface-sunken)',
    color: activeTab === tab ? 'white' : 'var(--text-secondary)',
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 1000 }}>
      <div className="ws-fade-up" style={panel}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 14 }}>
          <div>
            <h1 className="ws-display" style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <LucideIcon name="Settings" size={20} /> Admin Panel
            </h1>
            <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-secondary)' }}>Manage challenges and the AI model&rsquo;s sampling parameters.</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={() => setActiveTab('challenges')} className="btn-live" style={tabButtonStyle('challenges')}>
              Challenges
            </button>
            <button type="button" onClick={() => setActiveTab('llm')} className="btn-live" style={tabButtonStyle('llm')}>
              AI Config
            </button>
          </div>
        </div>
      </div>

      {activeTab === 'llm' && (
        <div className="ws-fade-up" style={{ ...panel, animationDelay: '60ms' }}>
          <h2 className="ws-display" style={{ margin: '0 0 4px', fontSize: 17, fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <LucideIcon name="Bot" size={19} /> AI Model Configuration
          </h2>
          <p style={{ margin: '0 0 18px', fontSize: 13, color: 'var(--text-secondary)' }}>
            Every Bedrock call in the app pulls its sampling parameters from one of these three profiles.
          </p>

          {llmError && (
            <div role="alert" className="ws-rise" style={{
              display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 14px', borderRadius: 10, marginBottom: 16,
              background: 'oklch(0.6 0.2 25 / 0.1)', border: '1px solid var(--danger, #b42318)',
            }}>
              <span style={{ color: 'var(--danger, #b42318)', flex: '0 0 auto', marginTop: 1 }}><LucideIcon name="AlertTriangle" size={16} /></span>
              <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5, color: 'var(--danger, #b42318)', flex: 1 }}>{llmError}</p>
              <button type="button" onClick={() => setLlmError('')} aria-label="Dismiss" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger, #b42318)', flex: '0 0 auto', padding: 0, display: 'flex' }}>
                <LucideIcon name="X" size={15} />
              </button>
            </div>
          )}

          {llmSuccess && (
            <div role="status" className="ws-rise" style={{
              display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 14px', borderRadius: 10, marginBottom: 16,
              background: 'oklch(0.65 0.15 155 / 0.12)', border: '1px solid var(--success)',
            }}>
              <span style={{ color: 'var(--success)', flex: '0 0 auto', marginTop: 1 }}><LucideIcon name="CheckCircle2" size={16} /></span>
              <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5, color: 'var(--success)', flex: 1 }}>{llmSuccess}</p>
              <button type="button" onClick={() => setLlmSuccess('')} aria-label="Dismiss" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--success)', flex: '0 0 auto', padding: 0, display: 'flex' }}>
                <LucideIcon name="X" size={15} />
              </button>
            </div>
          )}

          {llmLoading ? (
            <p className="ws-mono" style={{ margin: 0, fontSize: 12, color: 'var(--text-tertiary)' }}>Loading AI configuration&hellip;</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {CONFIG_SECTIONS.map((section, i) => {
                const params = llmConfig[section.key];
                return (
                  <div key={section.key} className="ws-fade-up" style={{ animationDelay: `${100 + i * 40}ms`, padding: 18, borderRadius: 12, border: '1px solid var(--border)', background: 'var(--surface-sunken)' }}>
                    <h3 style={{ margin: '0 0 3px', fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{section.title}</h3>
                    <p style={{ margin: '0 0 16px', fontSize: 12, color: 'var(--text-tertiary)' }}>{section.description}</p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 24px' }}>
                      <div>
                        <label style={sliderLabel}>Temperature: {params.temperature.toFixed(2)}</label>
                        <input type="range" min="0" max="1" step="0.01" value={params.temperature}
                          onChange={(e) => updateParam(section.key, 'temperature', parseFloat(e.target.value))}
                          style={{ width: '100%', accentColor: 'var(--ember)' }} />
                        <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--text-tertiary)' }}>Higher = more creative, lower = more focused</p>
                      </div>
                      <div>
                        <label style={sliderLabel}>Top-P: {params.top_p.toFixed(2)}</label>
                        <input type="range" min="0" max="1" step="0.01" value={params.top_p}
                          onChange={(e) => updateParam(section.key, 'top_p', parseFloat(e.target.value))}
                          style={{ width: '100%', accentColor: 'var(--ember)' }} />
                        <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--text-tertiary)' }}>Cumulative probability mass considered</p>
                      </div>
                      <div>
                        <label style={sliderLabel}>Top-K: {params.top_k}</label>
                        <input type="range" min="1" max="100" step="1" value={params.top_k}
                          onChange={(e) => updateParam(section.key, 'top_k', parseInt(e.target.value, 10))}
                          style={{ width: '100%', accentColor: 'var(--ember)' }} />
                        <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--text-tertiary)' }}>Limits vocabulary selection</p>
                      </div>
                      <div>
                        <label style={sliderLabel}>Max tokens: {params.max_tokens}</label>
                        <input type="range" min="100" max="4096" step="100" value={params.max_tokens}
                          onChange={(e) => updateParam(section.key, 'max_tokens', parseInt(e.target.value, 10))}
                          style={{ width: '100%', accentColor: 'var(--ember)' }} />
                        <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--text-tertiary)' }}>Maximum tokens in the response</p>
                      </div>
                    </div>
                  </div>
                );
              })}

              <button
                type="button"
                onClick={saveLLMConfig}
                disabled={savingLlm}
                className="btn-live ws-display"
                style={{
                  padding: '12px 20px', borderRadius: 10, border: 'none', fontSize: 14, fontWeight: 700,
                  cursor: savingLlm ? 'not-allowed' : 'pointer',
                  background: savingLlm ? 'var(--surface-sunken)' : 'var(--ember)',
                  color: savingLlm ? 'var(--text-tertiary)' : 'white',
                }}
              >
                {savingLlm ? 'Saving…' : 'Save AI Configuration'}
              </button>
            </div>
          )}
        </div>
      )}

      {activeTab === 'challenges' && (
        <div className="ws-fade-up" style={{ ...panel, animationDelay: '60ms' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18, flexWrap: 'wrap', gap: 10 }}>
            <h2 className="ws-display" style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <LucideIcon name="BookOpen" size={18} /> All Challenges
              {!challengesLoading && (
                <span className="ws-mono" style={{ fontSize: 10.5, color: 'var(--text-tertiary)', background: 'var(--surface-sunken)', border: '1px solid var(--border)', borderRadius: 999, padding: '2px 9px', fontWeight: 700 }}>
                  {challenges.length}
                </span>
              )}
            </h2>
            <button
              type="button"
              onClick={openNewChallenge}
              className="btn-live ws-display"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 18px', borderRadius: 10, border: 'none', fontSize: 13.5, fontWeight: 700, cursor: 'pointer', background: 'var(--ember)', color: 'white' }}
            >
              <LucideIcon name="Plus" size={15} /> New Challenge
            </button>
          </div>

          {challengesError && (
            <div role="alert" className="ws-rise" style={{
              display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 14px', borderRadius: 10, marginBottom: 16,
              background: 'oklch(0.6 0.2 25 / 0.1)', border: '1px solid var(--danger, #b42318)',
            }}>
              <span style={{ color: 'var(--danger, #b42318)', flex: '0 0 auto', marginTop: 1 }}><LucideIcon name="AlertTriangle" size={16} /></span>
              <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5, color: 'var(--danger, #b42318)', flex: 1 }}>{challengesError}</p>
              <button type="button" onClick={() => setChallengesError('')} aria-label="Dismiss" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger, #b42318)', flex: '0 0 auto', padding: 0, display: 'flex' }}>
                <LucideIcon name="X" size={15} />
              </button>
            </div>
          )}

          {challengesLoading ? (
            <p className="ws-mono" style={{ margin: 0, fontSize: 12, color: 'var(--text-tertiary)' }}>Loading challenges&hellip;</p>
          ) : challenges.length === 0 ? (
            <div className="ws-fade-up" style={{ textAlign: 'center', padding: '40px 20px' }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10, color: 'var(--text-tertiary)' }}><LucideIcon name="BookOpen" size={32} /></div>
              <h3 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>No challenges yet</h3>
              <p style={{ margin: '0 0 18px', fontSize: 13.5, color: 'var(--text-secondary)' }}>Create the first challenge to populate this track.</p>
              <button
                type="button"
                onClick={openNewChallenge}
                className="btn-live ws-display"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 18px', borderRadius: 10, border: 'none', fontSize: 13.5, fontWeight: 700, cursor: 'pointer', background: 'var(--ember)', color: 'white' }}
              >
                <LucideIcon name="Plus" size={15} /> New Challenge
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {challenges.map((challenge, i) => {
                const dStyle = DIFFICULTY_STYLE[challenge.difficulty];
                const confirming = confirmDeleteId === challenge.id;
                const deleting = deletingId === challenge.id;
                return (
                  <div
                    key={challenge.id}
                    className="ws-fade-up card-live"
                    style={{
                      animationDelay: `${60 + i * 20}ms`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap',
                      padding: '16px 18px', borderRadius: 12, background: 'var(--surface-sunken)', border: '1px solid var(--border)',
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <h3 style={{ margin: '0 0 7px', fontSize: 14.5, fontWeight: 700, color: 'var(--text-primary)' }}>
                        {challenge.title || <em style={{ color: 'var(--text-tertiary)', fontStyle: 'normal' }}>Untitled challenge</em>}
                      </h3>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <span className="ws-mono" style={{
                          padding: '2px 10px', borderRadius: 999, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em',
                          border: `1px solid ${dStyle.border}`, background: dStyle.bg, color: dStyle.fg,
                        }}>
                          {challenge.difficulty}
                        </span>
                        <span style={{ color: 'var(--text-tertiary)', fontSize: 12.5 }}>{challenge.category}</span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, color: 'var(--gold)' }}>
                          <LucideIcon name="star" size={12} /> {challenge.points} pts
                        </span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                      {confirming ? (
                        <>
                          <span style={{ fontSize: 12.5, color: 'var(--danger, #b42318)', fontWeight: 600 }}>Delete this challenge?</span>
                          <button
                            type="button"
                            onClick={() => handleDeleteChallenge(challenge.id)}
                            disabled={deleting}
                            className="btn-live"
                            style={{ padding: '7px 14px', fontSize: 12.5, fontWeight: 700, borderRadius: 8, border: 'none', cursor: deleting ? 'not-allowed' : 'pointer', background: 'var(--danger, #b42318)', color: 'white' }}
                          >
                            {deleting ? 'Deleting…' : 'Confirm'}
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmDeleteId(null)}
                            disabled={deleting}
                            className="btn-live"
                            style={{ padding: '7px 14px', fontSize: 12.5, fontWeight: 600, borderRadius: 8, border: '1px solid var(--border)', cursor: 'pointer', background: 'var(--surface)', color: 'var(--text-secondary)' }}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => openEditChallenge(challenge)}
                            className="btn-live"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 14px', fontSize: 12.5, fontWeight: 600, borderRadius: 8, border: '1px solid var(--border)', cursor: 'pointer', background: 'var(--surface)', color: 'var(--text-secondary)' }}
                          >
                            <LucideIcon name="Pencil" size={13} /> Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmDeleteId(challenge.id)}
                            className="btn-live"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 14px', fontSize: 12.5, fontWeight: 600, borderRadius: 8, border: '1px solid transparent', cursor: 'pointer', background: 'oklch(0.6 0.2 25 / 0.1)', color: 'var(--danger, #b42318)' }}
                          >
                            <LucideIcon name="Trash2" size={13} /> Delete
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {selectedChallenge && (
        <div
          onClick={() => !savingChallenge && closeModal()}
          style={{ position: 'fixed', inset: 0, background: 'oklch(0.16 0.018 264 / 0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 100 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="ws-pop-in"
            style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, maxWidth: 620, width: '100%', maxHeight: '90vh', overflowY: 'auto', padding: 26 }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 className="ws-display" style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>
                {isEditing ? 'Edit Challenge' : 'New Challenge'}
              </h2>
              <button
                type="button"
                onClick={closeModal}
                disabled={savingChallenge}
                aria-label="Close"
                className="btn-live"
                style={{ background: 'none', border: 'none', cursor: savingChallenge ? 'not-allowed' : 'pointer', color: 'var(--text-tertiary)', display: 'flex', padding: 4 }}
              >
                <LucideIcon name="X" size={20} />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={fieldLabel}>Title</label>
                <input
                  type="text"
                  value={selectedChallenge.title}
                  onChange={(e) => setSelectedChallenge({ ...selectedChallenge, title: e.target.value })}
                  style={fieldInput}
                />
              </div>

              <div>
                <label style={fieldLabel}>Description</label>
                <textarea
                  value={selectedChallenge.description}
                  onChange={(e) => setSelectedChallenge({ ...selectedChallenge, description: e.target.value })}
                  className="ws-mono"
                  style={{ ...fieldInput, height: 120, resize: 'vertical', lineHeight: 1.6 }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <label style={fieldLabel}>Difficulty</label>
                  <select
                    value={selectedChallenge.difficulty}
                    onChange={(e) => setSelectedChallenge({ ...selectedChallenge, difficulty: e.target.value as AdminChallenge['difficulty'] })}
                    style={{ ...fieldInput, cursor: 'pointer' }}
                  >
                    {difficulties.map((diff) => (
                      <option key={diff} value={diff}>{diff.charAt(0).toUpperCase() + diff.slice(1)}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={fieldLabel}>Category</label>
                  <select
                    value={selectedChallenge.category}
                    onChange={(e) => setSelectedChallenge({ ...selectedChallenge, category: e.target.value })}
                    style={{ ...fieldInput, cursor: 'pointer' }}
                  >
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <label style={fieldLabel}>Points</label>
                  <input
                    type="number"
                    value={selectedChallenge.points}
                    onChange={(e) => setSelectedChallenge({ ...selectedChallenge, points: parseInt(e.target.value, 10) || 0 })}
                    style={fieldInput}
                  />
                </div>

                <div>
                  <label style={fieldLabel}>Tags (comma-separated)</label>
                  <input
                    type="text"
                    value={selectedChallenge.tags.join(', ')}
                    onChange={(e) => setSelectedChallenge({ ...selectedChallenge, tags: e.target.value.split(',').map((tag) => tag.trim()) })}
                    style={fieldInput}
                  />
                </div>
              </div>

              {modalError && (
                <div role="alert" className="ws-rise" style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 14px', borderRadius: 10,
                  background: 'oklch(0.6 0.2 25 / 0.1)', border: '1px solid var(--danger, #b42318)',
                }}>
                  <span style={{ color: 'var(--danger, #b42318)', flex: '0 0 auto', marginTop: 1 }}><LucideIcon name="AlertTriangle" size={16} /></span>
                  <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5, color: 'var(--danger, #b42318)' }}>{modalError}</p>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 4 }}>
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={savingChallenge}
                  className="btn-live"
                  style={{ padding: '11px 20px', borderRadius: 10, border: '1px solid var(--border)', cursor: savingChallenge ? 'not-allowed' : 'pointer', background: 'var(--surface-sunken)', color: 'var(--text-secondary)', fontSize: 13.5, fontWeight: 600 }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveChallenge}
                  disabled={savingChallenge || !selectedChallenge.title.trim()}
                  className="btn-live ws-display"
                  style={{
                    padding: '11px 22px', borderRadius: 10, border: 'none', fontSize: 13.5, fontWeight: 700,
                    cursor: savingChallenge || !selectedChallenge.title.trim() ? 'not-allowed' : 'pointer',
                    background: savingChallenge || !selectedChallenge.title.trim() ? 'var(--surface-sunken)' : 'var(--ember)',
                    color: savingChallenge || !selectedChallenge.title.trim() ? 'var(--text-tertiary)' : 'white',
                  }}
                >
                  {savingChallenge ? 'Saving…' : isEditing ? 'Update Challenge' : 'Create Challenge'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
